import type { TaskContext } from "@pdfking/taskmaker"
import type { PdfEngineOptions } from "../types/engine/PdfEngineOptions"
import type { ZipFile } from "../types/files/cached/ZipFile"
import type { VirtualFile } from "../types/files/VirtualFile"
import type { EngineTasks } from "../types/tasks/EngineTasks"
import { BlobArray, BlobToUint8 } from "@pdfking/compatibility"
import { MapAsync, ZipCreate } from "@pdfking/tfunc"
import { GenerateFile } from "../files/GenerateFile"
import { RequestBlob } from "../files/RequestBlob"
import { ContentType } from "../types/files/ContentType"
import { VirtualFileType } from "../types/files/VirtualFileType"
import { TrackedTaskName } from "../types/tasks/TrackedTaskName"

export type ZipInput = {
  readonly content: VirtualFile
  readonly name: string
}

export const TaskZip = async function* (
  {
    emit,
    engine,
    options,
  }: TaskContext<
    EngineTasks,
    {
      readonly options: PdfEngineOptions
    }
  >,
  messages: AsyncGenerator<ZipInput>
): AsyncGenerator<ZipFile> {
  const files: Record<string, VirtualFile> = {}

  async function* toZip(): Parameters<typeof ZipCreate>[0] {
    let done = 0
    const load = (done = 0) =>
      emit({
        done,
        name: TrackedTaskName.COMPRESS,
        total: undefined,
      })

    void load(done)

    for await (const { content, name } of messages) {
      const { stream } = await RequestBlob(engine, options, content, emit)
      yield {
        chunks: MapAsync(BlobToUint8, stream),
        name,
      }
      files[name] = content

      void load((done += 1))
    }
  }

  const content = await GenerateFile(
    engine,
    options,
    MapAsync(a => BlobArray(a), ZipCreate(toZip())),
    ContentType.ZIP,
    emit
  )

  yield {
    content,
    enum: VirtualFileType.ZIP_FILE,
    file_meta: {},
    files,
  } as ZipFile
}
